import { useMemo } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { Button, Loading } from '@carbon/react';
import { Location, LocationFilled } from '@carbon/react/icons';
import { locatorStateAtom, toggleSelectedSubsurfaceMapAtom } from './atoms';
import type { SubsurfaceMapRow } from './types';
import styles from './map-locator-button.module.scss';

interface MapLocatorButtonProps {
  row: SubsurfaceMapRow;
  /** Called when the row becomes the selected map, to fit the map to it. */
  onSelect: (row: SubsurfaceMapRow) => void;
  /** Called when the row stops being the selected map. */
  onClear: () => void;
}

/**
 * A small spinner in the locator's icon slot while the map's cells load.
 * @return {JSX.Element} The spinner.
 */
const LoadingIcon = () => (
  <span className={styles.loadingIcon}>
    <Loading small withOverlay={false} description="Loading map" />
  </span>
);

/**
 * Radio-style locator on a Subsurface Maps row: selects the map (one at a time) or
 * clears it. Spins while the cells load and stays clickable to cancel; reads the
 * selection itself so selecting doesn't rebuild the table's columns.
 * @param {MapLocatorButtonProps} props The row and its select/clear callbacks.
 * @return {JSX.Element} The locator button.
 */
export const MapLocatorButton = ({
  row,
  onSelect,
  onClear,
}: MapLocatorButtonProps) => {
  const stateAtom = useMemo(() => locatorStateAtom(row.id), [row.id]);
  const state = useAtomValue(stateAtom);
  const toggle = useSetAtom(toggleSelectedSubsurfaceMapAtom);
  const selected = state !== 'idle';
  const loading = state === 'loading';
  // Stats built but no range: the map has no valid cells, so there is nothing to draw
  // (a map whose stats are pending stays selectable; its cells may well exist).
  const empty = row.statsBuiltAt !== null && row.valueMin === null;

  let icon = Location;
  let label = 'Show on map';
  if (empty) {
    label = 'No cells to show';
  } else if (loading) {
    icon = LoadingIcon as unknown as typeof Location;
    label = 'Loading map';
  } else if (selected) {
    icon = LocationFilled;
    label = 'Remove from map';
  }

  return (
    <Button
      kind="ghost"
      size="sm"
      renderIcon={icon}
      iconDescription={label}
      hasIconOnly
      isSelected={selected}
      aria-pressed={selected}
      aria-busy={loading}
      disabled={empty}
      onClick={() => {
        toggle(row);
        if (selected) onClear();
        else onSelect(row);
      }}
    />
  );
};
